// SPDX-License-Identifier: AGPL-3.0-or-later

import {Logger} from '@app/features/platform/utils/AppLogger';
import {getElectronAPI} from '@app/features/ui/utils/NativeUtils';
import {makeAutoObservable} from 'mobx';

const logger = new Logger('Window');

interface WindowSize {
	width: number;
	height: number;
}

const getWindowSize = (): WindowSize => ({
	width: window.innerWidth,
	height: window.innerHeight,
});
const getInitialFocused = (): boolean => document.hasFocus();
const getInitialVisible = (): boolean => !document.hidden;

function generateWindowId(): string {
	return `window-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

class Window {
	focused = getInitialFocused();
	visible = getInitialVisible();
	windowSize: WindowSize = getWindowSize();
	windowId: string = generateWindowId();
	lastFocusedAt: number = Date.now();
	createdAt: number = Date.now();
	private browserVisible = getInitialVisible();
	private nativeVisible: boolean | null = null;
	private nativeMobileVisible: boolean | null = null;

	constructor() {
		makeAutoObservable<this, 'browserVisible' | 'nativeVisible' | 'nativeMobileVisible'>(
			this,
			{browserVisible: false, nativeVisible: false, nativeMobileVisible: false},
			{autoBind: true},
		);
		this.initListeners();
		this.updateVisibility();
	}

	private initListeners(): void {
		window.addEventListener('focus', () => this.setFocused(true));
		window.addEventListener('blur', () => this.setFocused(false));
		document.addEventListener('visibilitychange', () => {
			this.setVisible(!document.hidden);
		});
		window.addEventListener('pagehide', () => this.setVisible(false));
		window.addEventListener('pageshow', () => this.setVisible(!document.hidden));
		document.addEventListener('freeze', () => this.setVisible(false));
		document.addEventListener('resume', () => this.setVisible(!document.hidden));
		window.addEventListener('resize', () => this.updateWindowSize());
		const electron = getElectronAPI();
		let nativeChanges = 0;
		electron?.onWindowVisibilityChange?.((visible) => {
			nativeChanges++;
			this.setNativeVisible(visible);
		});
		const generation = nativeChanges;
		void electron
			?.windowIsVisible?.()
			.then((visible) => {
				// A newer native event wins over an in-flight initial state query.
				if (nativeChanges === generation) this.setNativeVisible(visible);
			})
			.catch(() => {
				// Older desktop shells fall back to browser lifecycle signals.
			});
	}

	setFocused(focused: boolean): void {
		if (this.focused !== focused) {
			logger.debug(`Window focus changed: ${focused}`);
			this.focused = focused;
			if (focused) {
				this.lastFocusedAt = Date.now();
			}
		}
	}

	setVisible(visible: boolean): void {
		this.browserVisible = visible;
		this.updateVisibility();
	}

	private setNativeVisible(visible: boolean): void {
		this.nativeVisible = visible;
		this.updateVisibility();
	}

	/** Native mobile activation repairs browser visibility/focus that stayed stale across suspension. */
	setNativeMobileVisible(visible: boolean | null): void {
		this.nativeMobileVisible = visible;
		this.browserVisible = visible ?? !document.hidden;
		if (visible !== null) this.setFocused(visible);
		this.updateVisibility();
	}

	private updateVisibility(): void {
		const visible = this.browserVisible && this.nativeVisible !== false && this.nativeMobileVisible !== false;
		document.documentElement.classList.toggle('app-hidden', !visible);
		if (this.visible !== visible) {
			logger.debug(`Window visibility changed: ${visible}`);
			this.visible = visible;
		}
	}

	updateWindowSize(): void {
		this.windowSize = getWindowSize();
		logger.debug(`Window resized: ${this.windowSize.width}x${this.windowSize.height}`);
	}

	isFocused(): boolean {
		return this.focused;
	}

	isVisible(): boolean {
		return this.visible;
	}

	getWindowSize(): WindowSize {
		return this.windowSize;
	}
}

export default new Window();
